
import { bindDialog, BindDialogReturn, DialogProps, DialogResult, DialogState, useDialogState } from "../../../hooks/use-dialog-state/UseDialogState";
import htmlParse from 'html-react-parser';
import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import FullScreenDialog, { FullScreenDialogCloseReason } from "../../full-screen-dialog/FullScreenDialog";
import SettingsBar from "../../settings-bar/SettingsBar";
import { ContentContainer } from "../../content-container/ContentContainer";
import { ContentSection } from "../../content-section/ContentSection";
import LoadingZone from "../../loading-zone/LoadingZone";
import { GraphOptions, ITrendDataResultsModel } from "../../../services/trending-data-service/Models";
import { useObjectGraphFileStoreData } from "../../../queries/object-graph/UseObjectGraphFileStoreData";
import { useEffect, useRef, useState, type JSX } from "react";
import Graph, { GraphType } from "../../graph/Graph";
import ObjectGraphUtils from "../ObjectGraphUtils";
import { DataTooltipDisplayEnum } from "../../../services/user-settings-data/Models";
import { useUserContext } from "../../../contexts/user-context/UserContext";
import { useTranslation } from "react-i18next";
import { getObjectTypeName } from "../../../common/utilities/GetObjectTypeName";
import { format } from "date-fns";
import { TrendConfigurationModel } from "../../../services/trending-configuration-data-service/Models";
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import { useProgressContext } from "../../../contexts/progress-context/ProgressContext";
import { ActionTypeEnum, StatusEnum } from "../../../common/IExportNotificationMsg";
import ProgressIndicator from "../../progress-indicator/ProgressIndicator";
import { useObjectGraphData } from "../../../queries/object-graph/UseObjectGraphData";
import { DRCXConfigurationModelBase } from "../../../common/models/drcx/DRCXConfigurationModelBase";
import { IeLynxObject } from "../../../common/models/eLynxObject";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";

export type GraphViewDialogValues = {
    configuration?: TrendConfigurationModel;
    fileStoreID?: number;
    messageDate?: Date | undefined;
    title?: string;
}
export type GraphViewDialogResultValues = {};

export type GraphViewDialogResultReason = FullScreenDialogCloseReason | "ok";

export type GraphViewDialogResult = DialogResult<GraphViewDialogResultValues, GraphViewDialogResultReason>;

export type GraphViewDialogProps = DialogProps<GraphViewDialogValues, GraphViewDialogResult>;

export default function GraphViewDialog({
    open,
    onClose,
    values
}: GraphViewDialogProps): JSX.Element {

    const routeContext = useRouteContext();
    const { t } = useTranslation();
    const [userState] = useUserContext();
    const { confirm } = useConfirmation();
    const [fileStoreID, setFileStoreID] = useState<number | undefined>();
    const [progressState, actionStarted] = useProgressContext();
    const downloadUid = useRef<string>(undefined);
    const progress = progressState.items.find(o => o.uid === downloadUid.current);

    const graphRef = useRef<GraphType>(undefined);
    const [description, setDescription] = useState<string | undefined>(undefined);
    const [graphOptions, setGraphOptions] = useState<GraphOptions | undefined>(undefined);

    const {
        isFetching: isFetchingAdHocData
    } = useQueryNotifications(useObjectGraphData)({
        contextGroupID: routeContext.groupID as number,
        eLynxObject: routeContext.contextObject as IeLynxObject,
        configuration: values?.configuration,
        configurationID: DRCXConfigurationModelBase.NEW_CONFIGURATION_ID,
        duration: {
            startDate: undefined,
            endDate: undefined
        },
        displayOptions: undefined,
        enabled: values?.configuration !== undefined,
        onSuccess: (data: ITrendDataResultsModel | undefined) => {
            onFetchSuccess(data);
        },
        staleTime: 0,
        cacheTime: 0,
        keepPreviousData: false,
        refetchOnWindowFocus: false
    });

    const {
        isFetching: isFetchingFileStoreData,
    } = useQueryNotifications(useObjectGraphFileStoreData)({
        contextGroupID: routeContext.groupID as number,
        fileStoreID: fileStoreID as number,
        enabled: (fileStoreID ?? 0) > 0,
        onSuccess: (data: ITrendDataResultsModel | undefined) => {
            setFileStoreID(undefined);
            onFetchSuccess(data);
        },
        staleTime: 0,
        cacheTime: 0,
        keepPreviousData: false,
        refetchOnWindowFocus: false
    });

    // #region Effects

    useEffect(() => {

        if (values?.fileStoreID ||
            progress?.notificationMsg.status === StatusEnum.Completed ||
            progress?.notificationMsg.status === StatusEnum.Failed) {

            downloadUid.current = undefined;

            if (
                values?.fileStoreID ||
                (progress?.notificationMsg.action === ActionTypeEnum.Notify &&
                    progress?.notificationMsg.fileStoreID)
            ) {
                setFileStoreID(values?.fileStoreID ?? progress?.notificationMsg.fileStoreID);
            }
        }
    }, [
        values?.fileStoreID,
        progress?.notificationMsg.action,
        progress?.notificationMsg.fileStoreID,
        progress?.notificationMsg.status,
        setFileStoreID
    ]);

    // #endregion Effects

    async function onFetchSuccess(data: ITrendDataResultsModel | undefined) {

        if (data?.isMaxDataValuesExceeded === true) {

            // We exceeded the data value limit, so prompt the user to make a decision
            // on how to proceed
            setDescription(undefined);
            setGraphOptions(undefined);

            if (data?.estimatedDataValues && data?.maxDataValues) {

                // Prompt the user to decide whether to adjust the duration or edit the configuration
                await showMaxDataValuesLimitDialog(data.maxDataValues, data.estimatedDataValues);
            }
        } else {

            if (data?.isDataDeferred === true) {

                setDescription(undefined);
                setGraphOptions(undefined);

                downloadUid.current = data.notificationID;

                actionStarted({
                    uid: downloadUid.current as string,
                    objectID: routeContext.groupID as number,
                    objectType: eLynxObjectTypeEnum.Group,
                    title: description ?? "", //title,
                    notificationMsg: {
                        action: ActionTypeEnum.Notify,
                        notificationID: downloadUid.current as string,
                        status: StatusEnum.Started,
                        statusMessage: t("progress.sendingRequest"),
                        additionalInfo: "",
                        blobURI: "",
                        percentComplete: 0,
                        temporary: true
                    }
                });

            } else {
                // We didn't exceed the data value limit and execution wasn't deferred, so proceed as normal
                processGraphData(data);
            }
        }
    }

    async function processGraphData(data: ITrendDataResultsModel | undefined) {

        if (data && (data.trendViewModels?.length ?? 0) > 0) {

            if (data.displayOptions?.hasComparisonTimeframe) {
                data.sectionNames = ObjectGraphUtils.getComparisonSectionNames();
            }

            const graphOptions: GraphOptions | undefined = ObjectGraphUtils.getGraphOptions(data.eLynxObject, data,
                userState.userSettings?.trendingDataTooltipDisplay ?? DataTooltipDisplayEnum.Shared,
                undefined,
                undefined
            );

            if (values?.title) {
                setDescription(values.title);
            } else {
                setDescription(`${getObjectTypeName(data.eLynxObject?.objectType)} ${t("trending.labels.graphing")} - ${data.eLynxObject?.displayText} - ${t("trending.created")} ${values?.messageDate ? format(values?.messageDate as Date, "P pp") : ""}`);
            }

            setGraphOptions({ ...graphOptions } as GraphOptions);

        } else {
            setDescription(undefined);
            setGraphOptions(undefined);
        }
    }

    function handleOnClose(reason: GraphViewDialogResultReason) {
        onClose({ reason: reason });
    }

    async function showMaxDataValuesLimitDialog(dataValuesLimit: number, estimatedDataValues: number) {
        const million: number = 1000000;
        const dataValuesLimitMil: string = (dataValuesLimit / million).toFixed(0);
        const dataValuesEstimateMil: string = (estimatedDataValues / million).toFixed(2);

        const message: string = t("dialogs.configDataLimitsDialog.messagePreview", {
            numDataValues: dataValuesEstimateMil,
            dataLimit: dataValuesLimitMil
        });

        await confirm({
            dialogTitle: t("dialogs.configDataLimitsDialog.title"),
            confirmationBodyComponent: htmlParse(message) as JSX.Element,
            showConfirmationText: true,
            showYesButton: false,
            showNoButton: false,
            showCloseButton: true,
        });

        // can't do anything else, so might as well close the dialog
        handleOnClose("ok");
    }

    const showLoading = isFetchingAdHocData ||
        isFetchingFileStoreData;

    return (
        <FullScreenDialog open={open} onClose={handleOnClose}>
            <SettingsBar
                title={description ?? ""}
                onClose={() => handleOnClose("ok")}
                hideAction={true}
            />

            <ProgressIndicator showTemporary={true} />

            <LoadingZone isLoading={showLoading}>
                <ContentContainer>
                    <ContentSection>

                        {(!showLoading && graphOptions) &&
                            <Graph ref={graphRef} contextObject={routeContext.contextObject} options={graphOptions} showAxesToggle={true} />
                        }

                    </ContentSection>
                </ContentContainer>
            </LoadingZone>
        </FullScreenDialog>
    );
}

export type GraphViewDialogState = DialogState<GraphViewDialogValues, GraphViewDialogResult>;
export type BindGraphViewDialogReturn = BindDialogReturn<GraphViewDialogValues, GraphViewDialogResult>;

export function useGraphViewDialogState(): GraphViewDialogState {
    return useDialogState();
}

export function bindGraphViewDialog(props: GraphViewDialogState): BindGraphViewDialogReturn {
    return bindDialog(props);
}
